
import {
  NonExtendedConditionObjectType,
  UIConditionType,
} from '../../../../../../Types/UrvalTypes';
import { ValueConditionValues } from './ConditionValuesTypes/ValueConditionValues';
import BooleanConditionValues from './ConditionValuesTypes/BooleanConditionValues';
import ChecklistConditionValues from './ConditionValuesTypes/ChecklistConditionValues';
import TypeConditionValues from './ConditionValuesTypes/TypeConditionValues';
import TypeTreeConditionValueControl from './ConditionValuesControls/TypeTreeConditionValueControl';
import ConditionValuesControls from './ConditionValuesControls/ConditionValuesControls';
import { setCondition } from '../../Utils/UrvalConditionObjectUtils';
import { withStyles } from 'tss-react/mui';
import { useIsExtraSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import FilterConditionValues from './ConditionValuesTypes/FilterConditionValues';

interface IInnerProps extends IProps {
  isSmallScreen: boolean;
}

interface IProps {
  conditionObject: NonExtendedConditionObjectType;
  setConditionObject: (
    newConditionObject: NonExtendedConditionObjectType
  ) => void;
  deleteConditionObject: () => void;
  disabled: boolean;
  classes?: any;
}

const styles = {
  flexContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
};

const BaseConditionValues: React.FC<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const doSetCondition = (newCondition: UIConditionType) => {
    props.setConditionObject(setCondition(newCondition, props.conditionObject));
  };

  const renderConditionWithControls = (
    condition: React.ReactNode,
    index?: number,
    showAddButton?: boolean
  ) => (
    <div
      key={index ? index : undefined}
      className={props.classes.flexContainer}
    >
      {condition}
      {renderConditionValuesControls(index, showAddButton)}
    </div>
  );

  const renderConditionValues = (): React.ReactNode => {
    const { conditionObject, disabled } = props;
    if (
      conditionObject.type === 'String' ||
      conditionObject.type === 'Numeric'
    ) {
      return (
        <ValueConditionValues
          attribute={conditionObject.attribute}
          condition={conditionObject.condition[0]}
          setCondition={doSetCondition}
          disabled={disabled}
          renderControls={renderConditionValuesControls}
        />
      );
    } else if (conditionObject.type === 'Boolean') {
      return renderConditionWithControls(
        <BooleanConditionValues
          attribute={conditionObject.attribute}
          condition={conditionObject.condition}
          setCondition={doSetCondition}
          isSmallScreen={props.isSmallScreen}
          disabled={disabled}
        />
      );
    } else if (conditionObject.type === 'Filter') {
      return renderConditionWithControls(
        <FilterConditionValues
          attribute={conditionObject.attribute}
          condition={conditionObject.condition}
          setCondition={doSetCondition}
          isSmallScreen={props.isSmallScreen}
          disabled={disabled}
        />
      );
    } else if (conditionObject.type === 'Checklist') {
      return renderConditionWithControls(
        <ChecklistConditionValues
          attribute={conditionObject.attribute}
          condition={conditionObject.condition}
          setCondition={doSetCondition}
          isSmallScreen={props.isSmallScreen}
          disabled={disabled}
        />
      );
    } else if (conditionObject.type === 'TypeTree') {
      return renderConditionWithControls(
        <TypeConditionValues
          conditionObject={conditionObject}
          disabled={disabled}
        />
      );
    } else {
      return `Unknown condition object type: ${JSON.stringify(
        conditionObject
      )}`;
    }
  };

  const renderConditionValuesControls = (
    index?: number,
    showAddButton?: boolean
  ): React.ReactNode => {
    const {
      conditionObject,
      deleteConditionObject,
      setConditionObject,
      disabled,
    } = props;

    if (conditionObject.disableInUrval) {
      return null;
    }
    if (conditionObject.type === 'TypeTree') {
      return (
        <TypeTreeConditionValueControl
          deleteConditionObject={deleteConditionObject}
          conditionObject={conditionObject}
          setConditionObject={setConditionObject}
          businessType={conditionObject.typeTree}
          disabled={disabled}
        />
      );
    } else {
      return (
        <ConditionValuesControls
          conditionObject={conditionObject}
          deleteConditionObject={deleteConditionObject}
          setConditionObject={setConditionObject}
          index={index}
          showAddButton={showAddButton}
        />
      );
    }
  };

  return <> {renderConditionValues()} </>;
};

const ConditionValuesUnstyled: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const smallScreen = useIsExtraSmallScreen();
  return <BaseConditionValues {...props} isSmallScreen={smallScreen} />;
};

export const ConditionValues = withStyles(ConditionValuesUnstyled, styles);
